// Enkel typed fetch-wrapper med cookie-auth + CSRF double-submit.

interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

function csrfHeader(method: string): Record<string, string> {
  const safe = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
  if (safe) return {};
  const token = readCookie('pay_csrf');
  return token ? { 'X-Pay-CSRF': token } : {};
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      ...(init?.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...csrfHeader(method),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  const ct = res.headers.get('content-type') ?? '';
  const isJson = ct.includes('application/json');
  if (!res.ok) {
    const err: ApiError = isJson
      ? await res.json()
      : { error: res.statusText, message: await res.text(), statusCode: res.status };
    throw err;
  }
  return isJson ? ((await res.json()) as T) : ((await res.text()) as unknown as T);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
